import {
  STACK_UNIT_COUNT_OPTIONS,
  GRID_PHASE_OPTIONS,
  SITE_DETAILS_STEPS,
  SITE_DETAILS_STEPS_LIST,
  VERIFICATION_STATUS,
  VERIFICATION_STEP,
  ADDING_BATTERY_REASON_OPTIONS,
} from "constants/wizard";
import { isNil, orderBy } from "lodash";

export const getSiteCurrentStepNumber = (key) => {
  const item = SITE_DETAILS_STEPS_LIST.find((it) => it.key === key);
  return item ? item.step : 1;
};

export const getDisplayStepNumberForStep = (step, primary = true) => {
  if (primary) {
    return step;
  }
  const skippedSteps = [
    SITE_DETAILS_STEPS.OWNERACCOUNT,
    SITE_DETAILS_STEPS.ASGRID,
  ];
  const skipStepCount = skippedSteps.filter((it) => it < step).length;
  return step - skipStepCount;
};

export const siteStepActiveOrCompleted = (key, step) => {
  const item = SITE_DETAILS_STEPS_LIST.find((it) => it.key === key);
  return item && item.step >= step;
};

export const siteStepCompleted = (key, step) => {
  const item = SITE_DETAILS_STEPS_LIST.find((it) => it.key === key);
  return item && item.step > step;
};

export const getMeterVerificationTableData = (stepItem) => {
  const data = stepItem?.data;
  const list = [];
  if (data.length > 0) {
    if (!data[0].phase_c) {
      let header = {
        header: true,
        field1: "",
      };
      data
        .map((it) => it.name)
        .forEach((it, i) => {
          const key = `field${i + 2}`;
          header = {
            ...header,
            [key]: it,
          };
        });
      list.push(header);

      ["voltage", "current", "power"].forEach((key) => {
        const keyItem = data.map((it) => it.single_phase[key]);
        list.push({
          header: false,
          field1: key == "power" ? "Power (W)" : key,
          field2: Math.round(keyItem[0] * 100) / 100,
          field3: Math.round((keyItem.length > 1 ? keyItem[1] : 0) * 100) / 100,
        });
      });
    } else {
      const names = data.map((it) => it.name);
      let fields = {};
      names.forEach((it, i) => {
        fields = {
          ...fields,
          [`field${i + 2}`]: it.replace("_", " "),
        };
      });

      ["phase_a", "phase_b", "phase_c"].forEach((item, i) => {
        list.push({
          header: true,
          field1: item.replace("_", " "),
          ...fields,
          breakBefore: i > 0,
        });

        ["voltage", "current", "power_factor", "power"].forEach((key) => {
          let fields = {};
          names.forEach((name, iN) => {
            const dataItem = data.find((it) => it.name === name);
            const mv = dataItem[item];
            const keyItem = mv[key];
            fields = {
              ...fields,
              [`field${iN + 2}`]: Math.round(keyItem * 100) / 100,
            };
          });
          const labelMap = {
            power: "Power (W)",
          };

          list.push({
            header: false,
            field1: labelMap[key] || key,
            ...fields,
          });
        });
      });
    }
  }

  return list;
};

export const getExternalPvVerificationTableData = (data) => {
  const list = [];
  if (data) {
    if ("single_phase" in data) {
      list.push({
        header: true,
        field1: "",
        field2: "Values",
      });
      if (data) {
        ["voltage", "current", "power", "power_factor"].forEach((key) => {
          const keyItem = data.single_phase[key];
          list.push({
            header: false,
            field1: key == "power" ? "Power (W)" : key,
            field2: Math.round(keyItem * 100) / 100,
          });
        });
      }
    } else {
      ["phase_a", "phase_b", "phase_c"].forEach((item, i) => {
        list.push({
          header: true,
          field1: item.replace("_", " "),
          field2: "Values",
          breakBefore: i > 0,
        });

        ["voltage", "current", "power", "power_factor"].forEach((key) => {
          const mv = data[item];
          const keyItem = mv[key];
          list.push({
            header: false,
            field1: key == "power" ? "Power (W)" : key,
            field2: Math.round(keyItem * 100) / 100,
          });
        });
      });
    }
  }

  return list;
};

export const getPvVerificationTableData = (data) => {
  const list = [];
  if (!data) return list;

  ["pv1", "pv2"].forEach((item, i) => {
    if (!data[item]) return;
    list.push({
      header: true,
      field1: `Channel ${i + 1}`,
      field2: "Values",
      breakBefore: i > 0,
    });

    ["voltage", "current", "power"].forEach((key) => {
      const pv = data[item];
      const keyItem = pv[key];
      list.push({
        header: false,
        field1: key == "power" ? "Power (W)" : key,
        field2: Math.round(keyItem * 100) / 100,
      });
    });
  });

  return list;
};

export const getBackupCircuitVerificationTableData = (data) => {
  const list = [];
  list.push({
    header: true,
    field1: "",
    field2: "Values",
  });
  if (data) {
    ["voltage", "power"].forEach((key) => {
      const keyItem = data.single_phase[key];
      list.push({
        header: false,
        field1: key == "power" ? "Inverter Power (W)" : key,
        field2: keyItem !== null ? Math.round(keyItem * 100) / 100 : "-",
      });
    });
  }

  return list;
};

export const getFlexibleExportsVerificationTableData = (data) => {
  const list = [];
  list.push({
    header: true,
    field1: "",
    field2: "Values",
  });
  if (data) {
    ["voltage", "power"].forEach((key) => {
      const keyItem = data.single_phase[key];
      list.push({
        header: false,
        field1: key == "power" ? "Power (W)" : key,
        field2: Math.round(keyItem * 100) / 100,
      });
    });
  }

  return list;
};

//Summary

export const getLabel = (options, value) => {
  const item = options.find((it) => it.value === value);
  return item?.label || "";
};

export const getSiteAddressSummary = (siteDetails) => {
  return `${siteDetails?.street}, ${siteDetails?.city}, ${siteDetails?.state_name}, ${siteDetails?.postcode}`;
};

export const getDnspandPhaseSummary = (dnspOptions, siteDetails) => {
  return `${getLabel(dnspOptions, siteDetails?.dnsp)}, ${getLabel(
    GRID_PHASE_OPTIONS,
    siteDetails?.phases
  )}`;
};

export const getBackupConnectionSummary = (unitDetails) => {
  return unitDetails?.backup
    ? `Enabled, ${unitDetails?.reserved}% reserved capacity`
    : "Disabled";
};

export const getRegionSummary = (region, siteDetails) => {
  return getLabel(region, siteDetails?.region);
};

export const getExportLimitSummary = (unitDetails) => {
  return !isNil(unitDetails?.as4777?.export_limit)
    ? `Enabled, ${unitDetails?.as4777?.export_limit}W`
    : "Disabled";
};

export const getGenerationLimitSummary = (unitDetails) => {
  return !isNil(unitDetails?.as4777?.generation_limit)
    ? `Enabled, ${unitDetails?.as4777?.generation_limit}VA`
    : "Disabled";
};

export const powerRateLimitSummary = (unitDetails) =>
  `${unitDetails?.as4777?.wgra}%/min`;

export const getRemoteControlSummary = (unitDetails) => {
  return unitDetails?.drm0
    ? "DRM0 connected / enabled"
    : "DRM0 not connected / disabled";
};

export const getVoltWattSummary = (unitDetails) => {
  return `Volt-Watt ${unitDetails?.as4777?.voltwatt ? "enabled" : "disabled"}`;
};

export const getVoltVarSummary = (unitDetails) => {
  return `Volt-Var ${unitDetails?.as4777?.voltvar ? "enabled" : "disabled"}`;
};

export const getPowerFactorSummary = (unitDetails) => {
  return `Power Factor ${unitDetails?.as4777?.power_factor} 
  (${unitDetails?.as4777?.power_factor > 0 ? "Supplying" : "Absorbing"})`;
};

export const getReactivePowerSummary = (unitDetails) => {
  return `Reactive Power ${unitDetails?.as4777?.reactive_power} 
  (${unitDetails?.as4777?.reactive_power > 0 ? "Supplying" : "Absorbing"})`;
};

export const isSystemInitialisationDone = (stepData) => {
  const completed = stepData.find(
    (it) =>
      it.status === VERIFICATION_STATUS.completed &&
      it.step === VERIFICATION_STEP.SYSTEM_INITIALISATION
  );

  return !isNil(completed);
};

export const isVerificationDone = (stepData) => {
  return stepData
    .filter(
      (it) =>
        it.status !== VERIFICATION_STATUS.notRequired &&
        it.status !== VERIFICATION_STATUS.postponed &&
        it.status !== VERIFICATION_STATUS.skipped &&
        it.step !== VERIFICATION_STEP.CALIBRATION &&
        it.step !== VERIFICATION_STEP.COMMISIONING
    )
    .every((it) => it.status === VERIFICATION_STATUS.completed);
};

export const isCalibrationDone = (stepData) => {
  const completed = stepData.find(
    (it) =>
      it.status === VERIFICATION_STATUS.completed &&
      it.step === VERIFICATION_STEP.CALIBRATION
  );

  return !isNil(completed);
};

export const scrollToStep = (id, timeout = 500) => {
  setTimeout(() => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView();
      const modal = document.getElementById("modal-container");
      const wizardContainer = document.getElementById("wizard-modal");
      const scrollContainer = modal || wizardContainer;
      var scrolledY = element.offsetTop;

      if (scrolledY && scrollContainer?.scroll) {
        scrollContainer.scroll(0, scrolledY - 120);
      }
    }
  }, timeout);
};

export const getBatteryModuleSummary = (batteryModule) => {
  if (batteryModule.length === 0) return "-";
  return orderBy(batteryModule, ["module_number"])
    .map((it) => it.serial)
    .join(", ");
};

export const getStackUnitCount = (value) => {
  if (!value) return "";
  const item = STACK_UNIT_COUNT_OPTIONS.find((it) => it.value === value);
  return item;
};

export const getAddingBatteryReason = (value) => {
  if (!value) return "";
  const item = ADDING_BATTERY_REASON_OPTIONS.find((it) => it.value === value);
  return item;
};

export function dataURLtoFile(dataUrl, fileName) {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, { type: mime });
}
